import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation35() {
  const { lesson, course, accent } = findLesson("piano-foundation", "35");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Take Lesson 11's "Hit the Road Jack" (C minor descent: C → B♭ → A♭ → G) and improvise over it using the C minor blues scale.</Highlight>
        <Highlight>The C minor blues scale: <strong>C - E♭ - F - G♭ - G - B♭ - C</strong>. Six notes plus the octave. The G♭ is the "blue note".</Highlight>
        <Highlight>Slide through the blue note (G♭) — never park on it. It works as a passing tone between F and G in either direction.</Highlight>
        <Highlight>Use the LH boogie-style accompaniment from Lesson 20 (1-5-1-6 on each chord) to anchor the song while the right hand improvises.</Highlight>
        <Highlight>Treat improv as <strong>combining patterns you already know</strong>, not inventing new ideas — the spiral approach from Lesson 24.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Where this lesson sits" title="Bringing the course's tools into one song" accent={accent.hex}>
        <p>
          This lesson is the bridge between the Piano Foundation course and what comes
          next. It takes a familiar song — Lesson 11's "Hit the Road Jack" — and asks you
          to improvise on top of it using the blues-scale vocabulary from Lesson 20. Every
          tool of the course points here:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>The chord-and-bass setup from <strong>Lesson 11</strong>.</li>
          <li>The blues scale from <strong>Lesson 20</strong>.</li>
          <li>The pendulum and crossing technique from <strong>Lesson 23</strong>.</li>
          <li>The "create while you learn" mindset from <strong>Lesson 24</strong>.</li>
        </ul>
      </Insight>

      <Insight eyebrow="The blues scale" title="C minor blues — six notes plus octave" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "Eb4", color: "#3b82f6", label: "♭3" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "Gb4", color: "#ef4444", label: "♭5 BLUE" },
            { pitch: "G4", color: "#3b82f6", label: "5" },
            { pitch: "Bb4", color: "#3b82f6", label: "♭7" },
            { pitch: "C5", color: "#22c55e", label: "8" },
          ]}
          height={180}
        />

        <p className="prose-nsm text-sm">
          The two <span className="text-emerald-300">green</span> notes are the home tones —
          your "Sa" at the bottom and the octave at the top. The{" "}
          <span className="text-rose-300">red</span> note is G♭ — the blue note. It lives
          as a passing tone between F and G; sliding through it is what gives the blues
          its signature ache.
        </p>
      </Insight>

      <Insight eyebrow="The accompaniment" title="LH boogie · the 1-5-1-6 walk" accent={accent.hex}>
        <p>
          The right hand will be improvising — so the left hand carries the song. From
          Lesson 20, the LH boogie pattern walks <strong>1-5-1-6</strong> on each chord.
          For "Hit the Road Jack" in C minor:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          On C: C - G - C - A♭ (since we're in minor, the 6 is ♭6)<br />
          On B♭: B♭ - F - B♭ - G♭<br />
          On A♭: A♭ - E♭ - A♭ - F<br />
          On G: G - D - G - E♭
        </p>

        <Tip>
          If the descending bass walk feels too busy, simplify to <strong>just the root +
          fifth</strong> (Lesson 19's Oom-Pah pattern) on each chord change. Use the freed
          mental bandwidth for the right-hand improv.
        </Tip>
      </Insight>

      <Insight eyebrow="Improv pattern building blocks" title="Combine patterns you already know" accent={accent.hex}>
        <p>
          Improvisation isn't invention. It's a recombination of small phrases you already
          have in your hand. Five reusable shapes from the C minor blues scale:
        </p>

        <Steps>
          <Step>
            <strong>Step.</strong> Walk the scale up or down one note at a time. C - E♭ - F
            - G - B♭. Predictable, calm.
          </Step>
          <Step>
            <strong>Skip.</strong> Leap a third. C - F. E♭ - G. Adds openness.
          </Step>
          <Step>
            <strong>Repeat.</strong> Hit the same note 2-4 times, varying rhythm. The most
            underused improv tool — pure rhythm with no melodic motion.
          </Step>
          <Step>
            <strong>Slide through the blue.</strong> F - G♭ - G ascending. G - G♭ - F
            descending. Always treat G♭ as a passing tone, never a destination.
          </Step>
          <Step>
            <strong>Return.</strong> Walk away from C and walk back to it. C - E♭ - F - E♭ -
            C. Ends on home.
          </Step>
        </Steps>
      </Insight>

      <Insight eyebrow="A practice routine" title="One scale · five patterns · one chord cycle" accent={accent.hex}>
        <Steps>
          <Step>
            Lock the LH into a simple Oom-Pah on the four chords: C minor → B♭ → A♭ → G.
            Loop until autopilot.
          </Step>
          <Step>
            Bring in the right hand and play <em>only Step</em> patterns (walking up and
            down the blues scale). Four bars.
          </Step>
          <Step>
            Switch to <em>Skip</em> patterns for four bars. Then <em>Repeat</em>. Then
            <em> Slide</em>. Then <em>Return</em>.
          </Step>
          <Step>
            Mix all five patterns at will. The LH never stops. The RH wanders, comes back
            home, wanders again.
          </Step>
          <Step>
            Sing the chorus on top — "Hit the road, Jack." If the lyric and the improv
            both fit cleanly, you've got an improvised solo with a vocal line, all on the
            same instrument.
          </Step>
        </Steps>

        <KeyIdea>
          You don't need new ideas to improvise. You need a small library of patterns
          and the confidence to mix them.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The Foundation course closes here" title="Where to go next" accent={accent.hex}>
        <p>
          That's the end of the Piano Foundation course. Every lesson can be revisited
          (Lesson 24's "spiral" idea) — and most of them will read differently the second
          time round. From here, the natural next paths are:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>Piano Intermediate</strong> — extends the techniques here into more
            advanced finger drills, deeper chord territory, and longer-form pieces.
          </li>
          <li>
            <strong>Music Theory</strong> — the Know dimension. Scales, intervals, modes,
            roman-numeral analysis.
          </li>
          <li>
            <strong>Ear Training</strong> — the Hear dimension. How to find the scale of a
            song, how to count beats in real music, how to transcribe melodies.
          </li>
          <li>
            <strong>Sing & Play</strong> — apply the foundation to specific pop, gospel and
            folk songs you can perform.
          </li>
        </ul>

        <Tip>
          Keep a recording app on your phone. Whenever something interesting comes out of
          your hands, capture it. That's where your future songs come from.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-1"
        label="Foundation course · cycle back to Lesson 1"
        why="You've finished the Foundation course. Cycle back to any earlier lesson and you'll find new layers — every drill plays differently once your hands have lived through the whole curriculum."
      />
    </LessonShell>
  );
}
